// Hand-rolled HASH router for the owner app (and the shell). Routes look like  #/app/today?sheet=add&at=...
// Sheets are overlays that push ONE history entry (?sheet=name) so Back closes them and never leaves the screen.
import { useSyncExternalStore, useCallback, useMemo } from './react';
import { createElement } from './h';
import { currentRaw, go, hrefOf, ROUTE_EVENT, urlMode } from '../config/urlmode';

export interface Route {
  /** '/app/today' */
  path: string;
  /** ['app','today'] */
  segments: string[];
  query: Record<string, string>;
  raw: string;
}

export function parseHash(hash: string): Route {
  let h = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!h.startsWith('/')) h = `/${h}`;
  const q = h.indexOf('?');
  const path = (q >= 0 ? h.slice(0, q) : h).replace(/\/+$/, '') || '/';
  const query: Record<string, string> = {};
  if (q >= 0) new URLSearchParams(h.slice(q + 1)).forEach((v, k) => { query[k] = v; });
  return { path, segments: path.split('/').filter(Boolean), query, raw: h };
}

export function buildHash(path: string, query?: Record<string, string | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined && v !== '') sp.set(k, v);
  const qs = sp.toString();
  return `#${path}${qs ? `?${qs}` : ''}`;
}

/** URL for a route in the active mode ('#/x?y' in hash mode, '/x?y' in path mode). */
const buildUrl = (path: string, query?: Record<string, string | undefined>): string => hrefOf(buildHash(path, query));

/** The route path we navigated away from most recently (for in-page Back that agrees with browser Back). */
export let previousPath = '';
let cached: Route = parseHash(currentRaw());
const listeners = new Set<() => void>();
let sheetOpener: HTMLElement | null = null;
let sheetOpenerKey = '';
function restoreOpener(): void {
  const el = sheetOpener, key = sheetOpenerKey;
  sheetOpener = null; sheetOpenerKey = '';
  setTimeout(() => {
    if (typeof document === 'undefined') return;
    if (cached.query.sheet) return;
    const target = el && el.isConnected ? el : (key ? document.querySelector<HTMLElement>(`[data-testid="${key}"]`) : null);
    target?.focus?.({ preventScroll: true });
  }, 200);
}
function refresh(): void {
  const hadSheet = !!cached.query.sheet;
  refreshInner();
  if (hadSheet && !cached.query.sheet && sheetOpener) restoreOpener();
}
function refreshInner(): void {
  const raw = currentRaw();   // hash mode: location.hash (unchanged); path mode: pathname + search
  const next = raw.replace(/^#/, '') === cached.raw ? cached : parseHash(raw);
  if (next !== cached) { if (next.path !== cached.path) previousPath = cached.path; cached = next; for (const cb of [...listeners]) cb(); }
}
if (typeof window !== 'undefined') {
  window.addEventListener('hashchange', refresh);
  window.addEventListener('popstate', refresh);
  window.addEventListener(ROUTE_EVENT, refresh);   // path mode: pushState/replaceState announce themselves
}
function subscribe(cb: () => void): () => void { listeners.add(cb); refresh(); return () => void listeners.delete(cb); }
const getSnapshot = (): Route => cached;

export function useRoute(): Route {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Push a new history entry (default) or replace the current one. */
export function navigate(path: string, opts: { replace?: boolean; query?: Record<string, string | undefined> } = {}): void {
  const target = path.startsWith('#') ? path : buildHash(path, opts.query);
  if (urlMode() === 'path') { go(target, opts.replace ? { replace: true } : {}); return; }
  if (target === (location.hash || '#/')) return;
  if (opts.replace) location.replace(target);
  else location.hash = target;
}

export interface LinkProps { to: string; className?: string; children?: any; replace?: boolean; [k: string]: any }
export function Link({ to, replace, onClick, children, ...rest }: LinkProps) {
  const href = hrefOf(to);
  const handle = useCallback((e: any) => {
    onClick?.(e);
    if (replace && !e.defaultPrevented && !e.metaKey && !e.ctrlKey) { e.preventDefault(); navigate(href, { replace: true }); }
  }, [href, replace, onClick]);
  return createElement('a', { href, onClick: handle, ...rest }, children);
}

// ---------------------------------------------------------------- sheets (history-backed overlays)
const SHEET_STATE = 'plSheet';
const prefixed = (p: Record<string, string | undefined>): Record<string, string | undefined> => Object.fromEntries(Object.entries(p).map(([k, v]) => [`s_${k}`, v]));
function stripSheet(q: Record<string, string>): Record<string, string> {
  const out = { ...q };
  for (const k of Object.keys(out)) if (k === 'sheet' || k.startsWith('s_')) delete out[k];
  return out;
}

/** Opens a sheet by pushing `?sheet=name&...params` onto the current route. */
export function openSheet(name: string, params: Record<string, string | undefined> = {}): void {
  const r = cached;
  if (!r.query.sheet && typeof document !== 'undefined') {
    const a = document.activeElement as HTMLElement | null;
    sheetOpener = a && a !== document.body ? a : null;
    sheetOpenerKey = a?.getAttribute?.('data-testid') ?? '';
  }
  const q = { ...stripSheet(r.query), sheet: name, ...prefixed(params) };
  const target = buildUrl(r.path, q);
  history.pushState({ [SHEET_STATE]: true }, '', target);
  refresh();
}

/** Closes the current sheet. If we pushed it, go Back (so history stays clean); otherwise replace. */
export function closeSheet(): void {
  const r = cached;
  if (!r.query.sheet) return;
  if ((history.state as any)?.[SHEET_STATE]) { history.back(); return; }
  history.replaceState(null, '', buildUrl(r.path, stripSheet(r.query)));
  refresh();
}

/** Leaves the current page for `path` and closes any open sheet in the same tick (no overlay left over the nav). */
export function leaveSheet(path: string): void {
  sheetOpener = null; sheetOpenerKey = '';
  history.replaceState(null, '', buildUrl(path));
  refresh();
}

/** Switches the content of an open sheet without adding history (e.g. appointment -> move picker). */
export function swapSheet(name: string, params: Record<string, string | undefined> = {}): void {
  const r = cached;
  const q: Record<string, string | undefined> = { ...stripSheet(r.query), sheet: name, ...prefixed(params) };
  history.replaceState(history.state, '', buildUrl(r.path, q));
  refresh();
}

export function useSheet(): { name: string | undefined; params: Record<string, string> } {
  const r = useRoute();
  return useMemo(() => {
    const params: Record<string, string> = {};
    for (const [k, v] of Object.entries(r.query)) if (k.startsWith('s_')) params[k.slice(2)] = v;
    return { name: r.query.sheet, params };
  }, [r]);
}
